/**
 * MultiSelectQuestion — checkbox grid with an optional "Other:…" free-text
 * row. Value is serialised as a JSON string so the answers map stays a flat
 * `{qid: string}` mapping; an "Other" selection becomes either `"Other"` (no
 * text) or `"Other:their text"`.
 */
import { Input } from '@/components/ui/input';
import { CheckCircle } from 'lucide-react';

export const MultiSelectQuestion = ({ options, value, onChange, questionId }) => {
  let selected = [];
  let otherText = '';
  try {
    const parsed = JSON.parse(value || '[]');
    selected = Array.isArray(parsed) ? parsed : [];
  } catch { selected = []; }
  const otherEntry = selected.find(s => s.startsWith('Other:'));
  if (otherEntry) otherText = otherEntry.substring(6);
  const hasOther = selected.some(s => s === 'Other' || s.startsWith('Other:'));

  const toggle = (opt) => {
    let next;
    if (opt === 'Other') {
      if (hasOther) {
        next = selected.filter(s => s !== 'Other' && !s.startsWith('Other:'));
      } else {
        next = [...selected.filter(s => s !== 'Other' && !s.startsWith('Other:')), 'Other'];
      }
    } else {
      next = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
    }
    onChange(JSON.stringify(next));
  };

  const setOtherText = (text) => {
    const base = selected.filter(s => s !== 'Other' && !s.startsWith('Other:'));
    const entry = text.trim() ? `Other:${text}` : 'Other';
    onChange(JSON.stringify([...base, entry]));
  };

  return (
    <div className="space-y-2" data-testid={`multiselect-${questionId}`}>
      <div className="grid grid-cols-2 gap-2">
        {options.filter(o => o !== 'Other').map(opt => {
          const isSelected = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-all ${isSelected ? 'bg-[#002FA7]/10 border-[#002FA7] text-[#002FA7] font-medium' : 'border-[#E5E7EB] text-[#4B5563] hover:border-[#9CA3AF]'}`}
              data-testid={`ms-opt-${opt.replace(/[\s\/]/g, '-')}`}
            >
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#002FA7] border-[#002FA7]' : 'border-[#D1D5DB]'}`}>
                {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
              </div>
              {opt}
            </button>
          );
        })}
      </div>
      {options.includes('Other') && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => toggle('Other')}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-all w-full ${hasOther ? 'bg-[#002FA7]/10 border-[#002FA7] text-[#002FA7] font-medium' : 'border-[#E5E7EB] text-[#4B5563] hover:border-[#9CA3AF]'}`}
            data-testid="ms-opt-Other"
          >
            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${hasOther ? 'bg-[#002FA7] border-[#002FA7]' : 'border-[#D1D5DB]'}`}>
              {hasOther && <CheckCircle className="w-3 h-3 text-white" />}
            </div>
            Other
          </button>
          {hasOther && (
            <Input
              className="mt-2 text-sm"
              placeholder="Please specify..."
              value={otherText}
              onChange={e => setOtherText(e.target.value)}
              data-testid="ms-other-text"
            />
          )}
        </div>
      )}
      {selected.length > 0 && (
        <p className="text-[10px] text-[#9CA3AF]">
          {selected.filter(s => !s.startsWith('Other:')).length + (hasOther ? 1 : 0)} selected
        </p>
      )}
    </div>
  );
};
